import React, { useState, useEffect, useRef } from 'react';

export interface GalleryImage {
  id: string;
  src: string;
  alt: string;
}

const ImageCarousel: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Load images once
  useEffect(() => {
    const gallery: GalleryImage[] = [
      { id: '1', src: '/gallary/1.jpeg', alt: 'Gallery Image 1' },
      { id: '2', src: '/gallary/2.jpeg', alt: 'Gallery Image 2' },
      { id: '3', src: '/gallary/3.jpeg', alt: 'Gallery Image 3' },
      { id: '4', src: '/gallary/4.jpeg', alt: 'Gallery Image 4' },
      { id: '5', src: '/gallary/5.jpeg', alt: 'Gallery Image 5' },
    ];
    setImages(gallery);
  }, []);

  // Break images into pages of 3 for desktop, 1 for mobile
  const pages: GalleryImage[][] = [];
  const isMobile = window.innerWidth < 768; // 768px is typical mobile breakpoint
  const imagesPerPage = isMobile ? 1 : 3;
  
  for (let i = 0; i < images.length; i += imagesPerPage) {
    pages.push(images.slice(i, i + imagesPerPage));
  }

  const prevPage = () => setCurrentPage((p) => (p - 1 + pages.length) % pages.length);
  const nextPage = () => setCurrentPage((p) => (p + 1) % pages.length);

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextPage();
    }
    if (isRightSwipe) {
      prevPage();
    }
  };

  // Handle image click
  const handleImageClick = (image: GalleryImage) => {
    setSelectedImage(image);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedImage]);

  return (
    <div className="w-full max-w-6xl mx-auto py-6 px-4">
      <div className="flex items-center justify-center gap-4">
        {/* Left arrow - hidden on mobile, only if more than one page */}
        {pages.length > 1 && (
          <button
            onClick={prevPage}
            className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all flex-shrink-0 hidden md:block"
          >
            →
          </button>
        )}

        {/* Carousel content */}
        <div className="flex-1 max-w-4xl">
          {/* Grid - responsive: 1 col on mobile, 3 cols on desktop */}
          <div 
            ref={carouselRef}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 touch-pan-y"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {pages[currentPage]?.map((img) => (
              <div key={img.id} className="overflow-hidden rounded-lg flex justify-center">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-80 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleImageClick(img)}
                />
              </div>
            ))}
          </div>

          {/* Mobile page indicator */}
          <div className="md:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
            {currentPage + 1}/{pages.length}
          </div>
        </div>

        {/* Right arrow - hidden on mobile, only if more than one page */}
        {pages.length > 1 && (
          <button
            onClick={nextPage}
            className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all flex-shrink-0 hidden md:block"
          >
            ←
          </button>
        )}
      </div>

      {/* Dots */}
      <ul className="carousel-dots hidden md:flex justify-center mt-16 gap-2">
        {pages.map((_, i) => (
          <li key={i} className={i === currentPage ? 'active' : ''}>
            <button
              onClick={() => setCurrentPage(i)}
              tabIndex={0}
              type="button"
              className="w-3 h-3 rounded-full bg-blue-600 opacity-25 hover:opacity-50 transition-opacity"
            >
              <span className="sr-only">Go to slide {i + 1}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;